/**
 * shared/ExpandableText/ExpandableText.jsx
 * ════════════════════════════════════════════════════════════════
 * Блок тексту (HTML) що розкривається по кнопці "Читати далі".
 *
 * ВИКОРИСТАННЯ:
 *   import ExpandableText from '../shared/ExpandableText/ExpandableText';
 *
 *   // Простий текст:
 *   <ExpandableText text="Звичайний текст..." />
 *
 *   // HTML (з RichEditor):
 *   <ExpandableText html={bio} />
 *
 *   // З налаштуваннями:
 *   <ExpandableText
 *     html={bio}
 *     previewHeight={100}
 *     expandLabel="Читати далі"
 *     collapseLabel="Згорнути"
 *     style={{ fontSize: 14 }}
 *   />
 *
 * Props:
 *   html          {string}  — HTML вміст (dangerouslySetInnerHTML)
 *   text          {string}  — звичайний текст (альтернатива html)
 *   previewHeight {number}  — висота у px до розкриття, default 96
 *   expandLabel   {string}  — текст кнопки розгортання, default "Читати далі"
 *   collapseLabel {string}  — текст кнопки згортання, default "Згорнути"
 *   style         {object}  — стилі для контейнера тексту
 *   className     {string}  — CSS клас для контейнера тексту
 *   alwaysExpand  {boolean} — не обмежувати висоту (показати все одразу)
 * ════════════════════════════════════════════════════════════════
 */

import React, { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

var STYLES_INJECTED = false;
function injectStyles() {
    if (STYLES_INJECTED) return;
    STYLES_INJECTED = true;
    var s = document.createElement('style');
    s.textContent = [
        '.exp-text-content p { margin: 0 0 10px; }',
        '.exp-text-content p:last-child { margin-bottom: 0; }',
        '.exp-text-content h3 { font-size: 15px; font-weight: 700; margin: 14px 0 6px; color: #111827; }',
        '.exp-text-content ul, .exp-text-content ol { padding-left: 22px; margin: 8px 0; }',
        '.exp-text-content li { margin-bottom: 4px; }',
        '.exp-text-content strong { font-weight: 700; }',
        '.exp-text-content em { font-style: italic; }',
        '.exp-text-content u { text-decoration: underline; }',
    ].join('\n');
    document.head.appendChild(s);
}

const ExpandableText = function(props) {
    var html          = props.html          || '';
    var text          = props.text          || '';
    var previewHeight = props.previewHeight || 96;
    var expandLabel   = props.expandLabel   || 'Читати далі';
    var collapseLabel = props.collapseLabel || 'Згорнути';
    var style         = props.style         || {};
    var className     = props.className     || '';
    var alwaysExpand  = props.alwaysExpand  || false;

    var [expanded, setExpanded]       = useState(false);
    var [needsExpand, setNeedsExpand] = useState(false);
    var contentRef = useRef(null);

    useEffect(function() { injectStyles(); }, []);

    // Перевіряємо чи вміст вищий за previewHeight
    useEffect(function() {
        if (alwaysExpand) return;
        if (!contentRef.current) return;
        var el = contentRef.current;
        // Тимчасово знімаємо обмеження щоб виміряти реальну висоту
        el.style.maxHeight = 'none';
        var realHeight = el.scrollHeight;
        el.style.maxHeight = '';
        setNeedsExpand(realHeight > previewHeight + 20); // +20px буфер
    }, [html, text, previewHeight, alwaysExpand]);

    if (!html && !text) return null;

    var contentStyle = Object.assign({
        fontSize: 14,
        lineHeight: 1.75,
        color: '#374151',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease',
        maxHeight: alwaysExpand || expanded || !needsExpand ? 'none' : previewHeight,
    }, style);

    return (
        <div style={{ position: 'relative' }}>
            {/* Вміст */}
            {html ? (
                <div
                    ref={contentRef}
                    className={'exp-text-content ' + className}
                    style={contentStyle}
                    dangerouslySetInnerHTML={{ __html: html }}
                />
            ) : (
                <div
                    ref={contentRef}
                    className={className}
                    style={contentStyle}
                >
                    {text}
                </div>
            )}

            {/* Градієнт + кнопка "Читати далі" */}
            {needsExpand && !expanded && !alwaysExpand && (
                <div style={{
                    position: 'absolute',
                    bottom: 0, left: 0, right: 0,
                    height: 52,
                    background: 'linear-gradient(transparent, #fff)',
                    display: 'flex',
                    alignItems: 'flex-end',
                    justifyContent: 'center',
                    paddingBottom: 4,
                }}>
                    <button
                        type="button"
                        onClick={function() { setExpanded(true); }}
                        style={{
                            background: '#fff',
                            border: '1px solid #dee2e6',
                            borderRadius: 20,
                            padding: '3px 14px',
                            fontSize: 12,
                            color: '#0069ff',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 5,
                            boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronDown} style={{ fontSize: 11 }} />
                        {expandLabel}
                    </button>
                </div>
            )}

            {/* Кнопка "Згорнути" */}
            {needsExpand && expanded && !alwaysExpand && (
                <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    marginTop: 8,
                }}>
                    <button
                        type="button"
                        onClick={function() { setExpanded(false); }}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: 12,
                            color: '#6b7280',
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: 5,
                        }}
                    >
                        <FontAwesomeIcon icon={faChevronUp} style={{ fontSize: 11 }} />
                        {collapseLabel}
                    </button>
                </div>
            )}
        </div>
    );
};

export default ExpandableText;